import { route } from "@circle-react/lib/routes/define";

// Community-wide settings group — the catch-all bucket under the "Settings"
// section of the admin nav. Each route corresponds to one configuration
// surface (general info, custom domain, etc.).
// `/settings/discover` deliberately omitted — the page is just an iframe
// of the public https://discover.circle.so directory. There's nothing
// an admin can configure on it; it's an in-product preview of how the
// public Discover catalog looks. Surfacing this through navigate_to would
// confuse users asking about Discover listing settings (which don't exist
// here).

export const settingsRoutes = {
  theme: route("/settings/theme", {
    title: "Customize theme",
    requires: "admin",
    description: "Customize your community's theme and appearance",
  }),

  general: route("/settings/general", {
    title: "General settings",
    requires: "admin",
    description:
      "General community settings, organized in blocks. Manage general settings: community name, default language, and community ID. Visibility: community URL/slug (with a link to set up a custom domain), public vs. private toggle (with a custom member sign-up link when private), allow-visitor-signup toggle (public communities), and Circle desktop app access toggle. Branding: community icon, logo, default Open Graph image (used when the community link is shared on social media), brand color (light and dark mode), the reply-to email address used on notification emails, and hide Circle branding (white label). Default notifications: community-wide default email and in-app toggles for new community activity — comments, likes, replies, mentions, direct messages. Community discovery & visibility: enable community switcher and allow search engines to index member profiles. Communication and content: default search-result sorting, truncate post/comment body in email notifications, customize the call-to-action on private-post share links, allow members to upload media, allow members to record voice messages in posts, and allow members to go live. On plans that include Community AI: AI summaries (on-demand summaries for posts, comments, spaces), AI content writer (in-editor writing assistant with an optional community description), and automated transcriptions (with a language selector) for post and course videos. Member account control: allow members to deactivate their own account, hide emails on member profiles, enforce email verification (OTP) for new signups, and allow moderators to download event-attendee CSVs. Activity scores (plans that include Community AI): enable weekly ML-generated member scores and choose which factors count: Presence, Participation, Contribution, and Connection. Reviews: collect reviews from members and choose when to prompt them.",
  }),

  customDomain: route("/settings/custom_domain", {
    title: "Custom domain",
    requires: "admin",
    description:
      "Set up a custom domain (e.g. community.yoursite.com) by entering the domain and adding a CNAME record in your DNS provider pointing to the Circle subdomain. Includes a built-in CNAME validator and setup-domain action.",
  }),

  weeklyDigest: route("/settings/weekly_digest", {
    title: "Weekly digest",
    requires: "admin",
    description:
      "Configure the personalized weekly digest email sent to members every Thursday at 9am PT. Set the subject line (supports variables like community name and date range), set an introduction body (optionally updated weekly to use the digest as a newsletter), and toggle which sections appear: popular posts, popular comments, stats, new members.",
  }),

  embed: route("/settings/embed", {
    title: "Embed",
    requires: "admin",
    description:
      "Copy embed code snippets to add Circle widgets to external websites. Includes the Community widget (a floating community icon that opens a community modal in the bottom-right of an external site) and the Post widget (embed individual posts). Snippets are JS that paste into your site's <head>.",
  }),

  singleSignOn: route("/settings/sso", {
    title: "Single sign-on",
    requires: "admin",
    description:
      "Set up Single Sign-On via an OAuth provider (e.g. MemberSpace, Auth0, custom OAuth). Configure: enable SSO, OAuth state-param security, fallback for Circle-account members, button label for the sign-in screen, custom sign-up URL, and an option to skip the profile-confirmation step for first-time SSO members. NOT for SAML — this is OAuth-based SSO.",
  }),

  integrations: route("/settings/integrations", {
    title: "Integrations",
    requires: "admin",
    description: "Connect your community to external services.",
  }),

  conversionTracking: route("/settings/conversion_tracking", {
    title: "Conversion tracking",
    requires: "admin",
    description:
      "Manage purchase-event tracking for Meta and Google Analytics. Configure provider credentials, enablement, and paywall routing.",
  }),

  legal: route("/settings/legal", {
    title: "Legal",
    requires: "admin",
    description:
      "Manage legal notices shown during signup. Configure: legal email and legal address (shown inside ToS and privacy policy), Terms of Service with an optional 'Exhibit A' custom-terms toggle for additional clauses, and Privacy policy with three options (use Circle's template, write your own, or link to an external privacy policy page). New members must agree to these to sign up.",
  }),

  mobileApp: route("/settings/mobile_app", {
    title: "Mobile app",
    requires: "admin",
    description:
      "Runtime configuration for Circle's generic Communities iOS / Android apps — NOT the custom branded mobile app (that lives at /settings/branded_apps_builder_ui and is a separate product). This page controls which tabs appear in the bottom nav of the generic Communities apps (drag-and-drop reorder of Feed / Leaderboard / Spaces / Events / Courses plus custom tabs), whether members can sign in via the Communities app at all, and whether to show an app-download banner on mobile browsers and outbound emails. If the user asks about home tabs for the *branded* mobile app, route to /settings/branded_apps_builder_ui instead.",
  }),

  reviews: route("/settings/analytics/reviews", {
    title: "Reviews",
    requires: "admin",
    description:
      "Reviews dashboard: average rating, total reviews and response rate, average rating over time, rating distribution, and the full list of member reviews with search and filters. When review collection is off, shows a 'Reviews are disabled' banner whose 'Enable reviews' action leads to the Reviews block in General settings, where collection and the member prompt timing are managed.",
  }),
};
